import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Eye, EyeSlash, User, Briefcase, Envelope, Phone, Lock, Buildings,
  Sparkle, Tag, ArrowsLeftRight, ChartLineUp, UsersThree, CheckCircle, Handshake, MapPin
} from "@phosphor-icons/react";
import axios from "axios";

const LOGO_URL = "/bch-logo.png";

const BENEFITS = [
  { icon: Tag, color: "text-bch-teal", bg: "bg-bch-teal/15", label: "Exclusive category ownership — only one business per category per chapter" },
  { icon: ArrowsLeftRight, color: "text-violet-400", bg: "bg-violet-500/15", label: "Qualified referrals from fellow business owners in your network" },
  { icon: ChartLineUp, color: "text-emerald-400", bg: "bg-emerald-500/15", label: "Measurable ROI — track business given and earned through the platform" },
  { icon: UsersThree, color: "text-amber-400", bg: "bg-amber-500/15", label: "Weekly meetings to build strong relationships and grow together" },
];

/* ============================
   INVITE LANDING PAGE
   ============================ */
const InviteLandingPage = ({ chapterId, chapterName, category, inviterName, meetingId, inviterId }) => {
  const [form, setForm] = useState({ name: "", contact: "", business_name: "", interested_to_join: false });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [nextMeeting, setNextMeeting] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchMeeting = async () => {
      try {
        const res = await axios.get(`${API}/public/meetings/${chapterId}/next`);
        if (res.data) setNextMeeting(res.data);
      } catch {}
    };
    if (chapterId) fetchMeeting();
  }, [chapterId]);

  const effectiveMeetingId = meetingId || (nextMeeting ? nextMeeting.id : "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.contact.replace(/\D/g, "").length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!effectiveMeetingId) {
      toast.error("No upcoming meeting found for this chapter");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/visitors/register`, {
        ...form,
        category,
        chapter_id: chapterId,
        meeting_id: effectiveMeetingId,
        invited_by_id: inviterId || null
      });
      setSubmitted(true);
      toast.success("Registered successfully!");
    } catch (error) {
      const d = error.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map(e => e.msg || e).join(", ") : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="invite-landing-page">
      {/* Header */}
      <div className="px-6 py-5 flex items-center justify-between border-b border-white/5">
        <img src={LOGO_URL} alt="BCH Logo" className="h-20 w-auto object-contain" />
        <Link to="/login" className="text-white/60 text-sm hover:text-white transition-colors">
          Already a member? <span className="text-bch-teal font-medium">Sign in</span>
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center px-6 py-6 lg:py-10">
        <div className="w-full max-w-3xl text-center">
          {/* Invite badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-bch-teal/10 border border-bch-teal/20 rounded-full mb-5" data-testid="invite-badge">
            <Sparkle className="w-4 h-4 text-bch-teal" weight="fill" />
            <span className="text-bch-teal text-sm font-semibold">
              {inviterName ? `${inviterName} invited you!` : "You've been invited!"}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4 leading-tight tracking-tight">
            Visit <span className="text-bch-gold">{chapterName}</span> Chapter Meeting
          </h1>
          <div className="flex items-center gap-3 justify-center mb-6">
            <span className="text-white/50 text-lg">as</span>
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/[0.06] border border-white/10 rounded-xl">
              <Tag className="w-5 h-5 text-bch-gold" weight="fill" />
              <span className="text-white text-lg font-bold">{category} Visitor</span>
            </div>
          </div>

          <p className="text-white/50 text-base lg:text-lg max-w-xl mx-auto leading-relaxed mb-6">
            Connect with a powerful network of verified business owners. Unlock maximum potential for business growth through qualified referrals.
          </p>

          {/* CTA — Show form or Express Interest button */}
          {submitted ? (
            <div className="w-full max-w-md mx-auto py-12 px-8 bg-white/[0.03] border border-white/10 rounded-3xl" data-testid="interest-success">
              <div className="w-16 h-16 rounded-2xl bg-bch-teal/20 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-bch-teal" weight="fill" />
              </div>
              <h2 className="font-display text-2xl text-white mb-3">You're Registered!</h2>
              <p className="text-white/60 text-sm leading-relaxed">
                You've been added as a visitor for the <span className="text-bch-gold font-medium">{chapterName}</span> chapter meeting.
                The chapter president will mark your attendance when you arrive. See you there!
              </p>
            </div>
          ) : !showForm ? (
            <div className="space-y-6">
              <button
                onClick={() => setShowForm(true)}
                data-testid="express-interest-cta"
                className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-bch-teal to-emerald-500 text-white font-bold text-lg rounded-2xl shadow-xl shadow-bch-teal/20 hover:shadow-2xl hover:shadow-bch-teal/30 hover:-translate-y-1 transition-all duration-300"
              >
                <Handshake className="w-6 h-6" weight="fill" />
                Express Interest
              </button>
              <p className="text-white/30 text-xs">No account needed — takes 30 seconds</p>
            </div>
          ) : (
            <div className="w-full max-w-md mx-auto bg-white/[0.03] border border-white/10 rounded-3xl p-8 animate-in fade-in slide-in-from-bottom-4" data-testid="interest-form-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-bch-teal to-emerald-500 flex items-center justify-center">
                  <Handshake className="w-5 h-5 text-white" weight="fill" />
                </div>
                <div className="text-left">
                  <h2 className="font-display text-lg text-white">Register as Visitor</h2>
                  <p className="text-white/50 text-xs">Fill your details to attend the meeting</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 text-left">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-white/70 text-sm">Full Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" weight="duotone" />
                    <Input id="name" type="text" value={form.name}
                      onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                      placeholder="Your full name" required data-testid="interest-name-input"
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-bch-teal" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact" className="text-white/70 text-sm">Mobile Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" weight="duotone" />
                    <Input id="contact" type="tel" value={form.contact}
                      onChange={(e) => setForm(p => ({ ...p, contact: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                      placeholder="9876543210" required data-testid="interest-phone-input"
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-bch-teal" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="business_name" className="text-white/70 text-sm">Business Name *</Label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" weight="duotone" />
                    <Input id="business_name" type="text" value={form.business_name}
                      onChange={(e) => setForm(p => ({ ...p, business_name: e.target.value }))}
                      placeholder="Your business name" required data-testid="interest-business-input"
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-bch-teal" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-white/70 text-sm">Category</Label>
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-white/30" weight="duotone" />
                    <Input type="text" value={category} disabled
                      className="pl-10 bg-white/5 border-white/10 text-white/60 cursor-not-allowed" />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-white/[0.03] border border-white/8 rounded-xl">
                  <input type="checkbox" id="interested_to_join" checked={form.interested_to_join}
                    onChange={(e) => setForm(p => ({ ...p, interested_to_join: e.target.checked }))}
                    className="w-4 h-4 rounded border-white/20 accent-bch-teal" data-testid="interest-join-checkbox" />
                  <Label htmlFor="interested_to_join" className="text-white/70 text-sm cursor-pointer">
                    I'm interested in joining this chapter as a member
                  </Label>
                </div>
                <Button type="submit" disabled={loading} data-testid="interest-submit-btn"
                  className="w-full bg-gradient-to-r from-bch-teal to-emerald-500 hover:from-bch-teal/90 hover:to-emerald-500/90 text-white font-bold py-3 rounded-xl transition-all duration-300 mt-2">
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Submitting...
                    </span>
                  ) : "Register for Meeting"}
                </Button>
              </form>
              <p className="text-white/30 text-[11px] text-center mt-5 leading-relaxed">
                By registering, you'll be added to the visitor list. The chapter president will mark your attendance on the meeting day.
              </p>
            </div>
          )}
        </div>

        {/* Meeting Details — above benefits */}
        {nextMeeting && nextMeeting.location && (
          <div className="w-full max-w-3xl mt-16 bg-white/[0.03] border border-white/8 rounded-2xl p-6" data-testid="invite-venue-card">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-bold mb-3">Meeting Details</p>
            <p className="text-white font-semibold text-base mb-1">{nextMeeting.title}</p>
            <p className="text-white/50 text-sm mb-4">{nextMeeting.meeting_date} &middot; {nextMeeting.meeting_time}</p>
            {(() => {
              const parts = nextMeeting.location.split(" | ");
              const venue = parts[0];
              const address = parts[1] && !parts[1].startsWith("http") ? parts[1] : null;
              const mapsLink = parts.find(p => p.startsWith("http"));
              return (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-rose-400 shrink-0" weight="fill" />
                    <span className="text-white/80 text-sm font-medium">{venue}</span>
                  </div>
                  {address && <p className="text-white/40 text-xs ml-6">{address}</p>}
                  {mapsLink && (
                    <a href={mapsLink} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 ml-6 mt-1 px-3 py-1.5 bg-blue-500/15 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-500/25 transition-colors"
                      data-testid="invite-maps-link">
                      <MapPin className="w-3 h-3" weight="fill" /> Open in Google Maps
                    </a>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Benefits — below meeting details */}
        <div className="w-full max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-start gap-3.5 p-4 bg-white/[0.02] border border-white/5 rounded-2xl">
                <div className={`w-9 h-9 rounded-lg ${b.bg} flex items-center justify-center shrink-0`}>
                  <Icon className={`w-4.5 h-4.5 ${b.color}`} weight="duotone" />
                </div>
                <p className="text-white/60 text-sm leading-relaxed">{b.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ============================
   REGULAR SIGNUP PAGE
   ============================ */
export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const inviteChapterId = searchParams.get("chapter_id") || "";
  const inviteChapterName = searchParams.get("chapter") || "";
  const inviteCategory = searchParams.get("category") || "";
  const inviterName = searchParams.get("inviter") || "";
  const inviteMeetingId = searchParams.get("meeting_id") || "";
  const inviterId = searchParams.get("inviter_id") || "";
  const isInvite = !!(inviteChapterName && inviteCategory);

  const [formData, setFormData] = useState({
    full_name: "",
    business_name: "",
    business_category: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: ""
  });
  const [categories, setCategories] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { signup, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
    if (!isInvite) fetchCategories();
  }, [isAuthenticated, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.phone.replace(/\D/g, "").length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    setLoading(true);
    
    try {
      const { confirmPassword, ...submitData } = formData;
      await signup(submitData);
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg || e).join(", ") : "Signup failed. Please try again.";
      toast.error("Signup Failed", { description: message });
    } finally {
      setLoading(false);
    }
  };

  // Show invite landing page instead of signup form
  if (isInvite) {
    return <InviteLandingPage chapterId={inviteChapterId} chapterName={inviteChapterName} category={inviteCategory} inviterName={inviterName} meetingId={inviteMeetingId} inviterId={inviterId} />;
  }

  // Show thank you screen after successful submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-8" data-testid="signup-success-screen">
        <div className="w-full max-w-md text-center">
          <div className="mb-8 flex justify-center">
            <img src={LOGO_URL} alt="BCH Logo" className="h-20 w-auto object-contain" />
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-10">
            <div className="w-18 h-18 rounded-2xl bg-bch-teal/20 flex items-center justify-center mx-auto mb-6 w-[72px] h-[72px]">
              <CheckCircle className="w-10 h-10 text-bch-teal" weight="fill" />
            </div>
            <h2 className="font-display text-2xl text-white mb-3">Thank You for Applying!</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              Your membership application has been submitted successfully.
            </p>
            <div className="bg-bch-teal/10 border border-bch-teal/20 rounded-xl p-4">
              <div className="flex items-center gap-2 justify-center mb-2">
                <Envelope className="w-5 h-5 text-bch-teal" weight="duotone" />
                <p className="text-bch-teal font-semibold text-sm">Check Your Email</p>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                You will receive a confirmation email on your registered email address once your application is reviewed and approved by the admin.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      <SEO title="Apply for Membership" description="Apply to join BCH Networking, India's exclusive business networking platform for verified owners. Connect with professionals in your city, exchange referrals, and attend bi-weekly structured meetings." path="/signup" />
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.pexels.com/photos/5778708/pexels-photo-5778708.jpeg')",
            filter: "brightness(0.3)"
          }}
        />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <img src={LOGO_URL} alt="BCH Logo" className="h-20 w-auto object-contain" />
          <div>
            <h1 className="font-display text-5xl text-white mb-4">
              Join the Network
            </h1>
            <p className="text-white/70 text-lg max-w-md mb-6">
              BCH membership gives you access to a curated network of business owners committed to mutual growth through referrals.
            </p>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-bch-teal/20 flex items-center justify-center">
                  <Buildings className="w-5 h-5 text-bch-teal" weight="duotone" />
                </div>
                <p className="text-white/80">Exclusive category ownership per chapter</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-bch-gold/20 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-bch-gold" weight="duotone" />
                </div>
                <p className="text-white/80">Measurable ROI with referral tracking</p>
              </div>
            </div>
          </div>
          <div />
        </div>
      </div>

      {/* Right side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src={LOGO_URL} alt="BCH Logo" className="h-12 w-auto object-contain" />
          </div>
          
          <div className="text-center mb-8">
            <h2 className="font-display text-3xl text-white mb-2">Apply for Membership</h2>
            <p className="text-white/60">Fill in your details to request access</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-white/80">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="full_name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleChange("full_name", e.target.value)}
                  placeholder="John Doe"
                  required
                  data-testid="signup-name-input"
                  className="pl-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_name" className="text-white/80">Business Name</Label>
              <div className="relative">
                <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="business_name"
                  type="text"
                  value={formData.business_name}
                  onChange={(e) => handleChange("business_name", e.target.value)}
                  placeholder="Your Business Name"
                  required
                  data-testid="signup-business-input"
                  className="pl-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="business_category" className="text-white/80">Business Category</Label>
              <Select value={formData.business_category} onValueChange={(value) => handleChange("business_category", value)}>
                <SelectTrigger data-testid="signup-category-select" className="bg-bch-surface border-white/10 text-white focus:border-bch-teal">
                  <Briefcase className="w-5 h-5 text-white/40 mr-2" weight="duotone" />
                  <SelectValue placeholder="Select your category" />
                </SelectTrigger>
                <SelectContent className="bg-bch-card border-white/10">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.name} className="text-white hover:bg-bch-teal/20">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/80">Email Address</Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="signup-email-input"
                  className="pl-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-white/80">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    handleChange("phone", val);
                  }}
                  placeholder="9876543210"
                  required
                  maxLength={10}
                  pattern="\d{10}"
                  data-testid="signup-phone-input"
                  className="pl-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
              </div>
              {formData.phone && formData.phone.length !== 10 && (
                <p className="text-red-400 text-xs mt-1">Phone number must be exactly 10 digits</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white/80">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  data-testid="signup-password-input"
                  className="pl-10 pr-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" weight="duotone" /> : <Eye className="w-5 h-5" weight="duotone" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white/80">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" weight="duotone" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Confirm your password"
                  required
                  data-testid="signup-confirm-password-input"
                  className="pl-10 bg-bch-surface border-white/10 text-white placeholder:text-white/40 focus:border-bch-teal"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-button"
              className="w-full bg-bch-gold hover:bg-bch-gold-dark text-black font-bold py-3 rounded-sm transition-colors duration-300 mt-6"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Submitting...
                </span>
              ) : (
                "Submit Application"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-white/60">
              Already have an account?{" "}
              <Link to="/login" className="text-bch-teal hover:text-bch-teal/80 font-medium transition-colors" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
